// "View More" on the category/collection grid (main-collection-product-grid.liquid)
// — fetches the next paginated page's rendered section HTML (same
// ?section_id= pattern facets.js uses for filtering) and appends its
// products into the current page's grid instead of navigating to a new
// page.
//
// One product can expand into several color cards (see card-product.liquid's
// card_variant param), so a fixed-size page of real Shopify products doesn't
// reliably translate into a round number of visual cards — a batch can land
// on, say, 21 or 26 instead of a clean multiple of the grid's own column
// count, leaving a lone card stranded on its own row.
//
// Rather than fetching MORE and MORE pages hoping to stumble onto a clean
// total (which can spiral into dozens of fetches on a heavily color-split
// collection, dumping far more products into view than expected), this
// works the other way: show only as many cards as make full rows, and hold
// the small leftover back in memory to be shown FIRST on the next "View
// More" click (or right after page load, for the very first page), before
// fetching anything new. Nothing is ever lost or duplicated — it's just
// deferred by one click — and each interaction only ever costs exactly one
// extra page fetch, never an unbounded chain. The one place a short row can
// still appear is the true end of the collection, where there's nothing
// left to borrow from to complete it.
(function () {
  const CARD_SELECTOR = 'li.grid__item, li.bento-grid__featured, li.bento-grid__small';
  // The bento "big card" blocks (component-collection-bento-grid.css) are
  // NOT simple wrapped rows — they're a fixed, self-contained 1-big+4-small
  // visual unit that main-collection-product-grid.liquid's Liquid always
  // renders as a complete block (card_count >= 10/15 triggers one, and it's
  // always exactly 5 items when it does). Row-completion logic has no
  // business touching those cards at all: counting them toward a "multiple
  // of columnsPerRow" check, or worse, carrying one away to show later in a
  // plain grid list (where its bento-specific sizing CSS no longer applies
  // to the block it assumed it was part of), is what caused oversized/
  // misplaced cards after a filter+View More combination. Only cards in
  // the ordinary flex-wrap grid lists count for this purpose.
  const PLAIN_CARD_SELECTOR = 'li.grid__item';
  // Matches the "min-width: 990px" breakpoint component-card grid CSS
  // (base.css) switches from columns_mobile to columns_desktop at.
  const DESKTOP_BREAKPOINT = 990;

  function getColumnsPerRow(el) {
    const isDesktop = window.innerWidth >= DESKTOP_BREAKPOINT;
    const raw = isDesktop ? el.dataset.columnsDesktop : el.dataset.columnsMobile;
    const parsed = parseInt(raw, 10);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : null;
  }

  // The page-1 grid isn't always ONE <ul> — once a collection has 10+ cards
  // (main-collection-product-grid.liquid's bento pattern), it's split into
  // several sibling lists: an ordinary row, a bento-grid--left/--right
  // block, and then another ordinary <ul.grid> for anything past the 15th
  // card — but that trailing plain list only exists once there are MORE
  // than 15 cards. With 10-14, the bento block itself is the very last
  // list before the View More wrapper. Only a genuine PLAIN list counts as
  // the "tail" here — copying a .bento-grid block's own classes onto a
  // fresh list of ordinary cards packed it into a container built to hold
  // exactly 5 (1 big + 4 small), breaking its layout entirely.
  function getTailList(wrapper) {
    const sibling = wrapper.previousElementSibling;
    return sibling && sibling.tagName === 'UL' && sibling.classList.contains('grid') ? sibling : null;
  }

  // Per-section carry-over state: cards held back from the last reveal
  // (because showing them would've left an incomplete row) plus the real
  // "next page to fetch" cursor, which this script manages itself from
  // here on rather than trusting the wrapper's own data-next-url (that
  // always reflects Shopify's raw next page, not where this carry-over
  // logic has actually gotten to).
  const state = {};

  function getState(sectionId, initialNextUrl) {
    if (!state[sectionId]) {
      state[sectionId] = { carryCards: [], nextUrl: initialNextUrl };
    }
    return state[sectionId];
  }

  async function fetchOnePage(nextUrl, sectionId) {
    const separator = nextUrl.includes('?') ? '&' : '?';
    const response = await fetch(`${nextUrl}${separator}section_id=${sectionId}`);
    if (!response.ok) throw new Error(`Request failed: ${response.status}`);
    const html = await response.text();
    const nextDocument = new DOMParser().parseFromString(html, 'text/html');
    const nextContainer = nextDocument.querySelector(`#product-grid[data-id="${sectionId}"]`);
    if (!nextContainer) throw new Error('Could not find the next page\'s product grid');

    const cards = [];
    nextContainer.querySelectorAll(CARD_SELECTOR).forEach((item) => {
      item.className = 'grid__item';
      cards.push(item);
    });

    const nextWrapper = nextContainer.querySelector('.product-grid-view-more');
    return { cards, nextUrl: nextWrapper ? nextWrapper.dataset.nextUrl : null };
  }

  // Builds a proper bento-right block (component-collection-bento-grid.css)
  // out of exactly 5 plain cards, replicating main-collection-product-grid.
  // liquid's own class assignment for items 11-15: the LAST of the 5 is the
  // big "featured" card (i == 15), and the 4th is also marked for the
  // mobile-only full-width row pattern (i == 14).
  function buildBentoRightList(cards) {
    const list = document.createElement('ul');
    list.className = 'bento-grid bento-grid--right';
    list.setAttribute('role', 'list');
    cards.forEach((card, index) => {
      const isFeatured = index === cards.length - 1;
      const isMobileFull = index === cards.length - 2;
      card.className = isFeatured ? 'bento-grid__featured' : 'bento-grid__small';
      if (isMobileFull) card.classList.add('mobile-grid__full');
      list.appendChild(card);
    });
    return list;
  }

  // A filtered view can easily have enough matches to trigger row 2's
  // bento-left block (10+ cards) without having enough on that SAME
  // Shopify page to also reach row 3's bento-right block (15+) — Liquid
  // only ever sees one page's own card count at a time. This fetches
  // ahead (bounded, same one-section-at-a-time cost as everything else
  // here) to gather 5 more cards and build that second bento block
  // properly, instead of just leaving row 3 as a plain, possibly short,
  // row. If the collection genuinely doesn't have 15 matching cards at
  // all, this gracefully gives up and leaves the gathered cards for the
  // normal plain-row handling instead.
  async function completeBentoRight(sectionId, bentoLeft, columnsPerRow) {
    const afterLeft = bentoLeft.nextElementSibling;
    if (afterLeft && afterLeft.classList && afterLeft.classList.contains('bento-grid--right')) return;

    const wrapper = document.querySelector(`.product-grid-view-more[data-section-id="${sectionId}"]`);
    const carryState = getState(sectionId, wrapper ? wrapper.dataset.nextUrl : null);

    let pool = carryState.carryCards.splice(0, carryState.carryCards.length);
    const MAX_FETCHES_FOR_BENTO = 3;
    for (let fetchCount = 0; pool.length < 5 && carryState.nextUrl && fetchCount < MAX_FETCHES_FOR_BENTO; fetchCount += 1) {
      // eslint-disable-next-line no-await-in-loop
      const { cards, nextUrl } = await fetchOnePage(carryState.nextUrl, sectionId);
      pool = pool.concat(cards);
      carryState.nextUrl = nextUrl;
    }

    if (pool.length < 5) {
      // Not enough matching products exist to ever complete a bento-right
      // block — hand whatever was gathered back for the ordinary plain-row
      // carry-over logic to deal with instead.
      carryState.carryCards = pool;
      return;
    }

    const bentoRightList = buildBentoRightList(pool.slice(0, 5));
    let afterBento = pool.slice(5);
    let insertAfter = bentoRightList;
    bentoLeft.insertAdjacentElement('afterend', bentoRightList);

    // The same fetches that gathered the bento-right cards often bring back
    // enough extra cards to fill row 4 (and beyond) immediately too — show
    // every full row available right now instead of deferring all of it to
    // the next "View More" click, only carrying forward a true remainder
    // smaller than one row.
    if (columnsPerRow && afterBento.length >= columnsPerRow) {
      const fullRowCount = afterBento.length - (afterBento.length % columnsPerRow);
      const plainList = document.createElement('ul');
      let gridClass = 'grid product-grid';
      if (wrapper) {
        gridClass += ` grid--${wrapper.dataset.columnsMobile}-col-tablet-down grid--${wrapper.dataset.columnsDesktop}-col-desktop`;
      }
      plainList.className = gridClass;
      plainList.setAttribute('role', 'list');
      afterBento.slice(0, fullRowCount).forEach((card) => plainList.appendChild(card));
      insertAfter.insertAdjacentElement('afterend', plainList);
      insertAfter = plainList;
      afterBento = afterBento.slice(fullRowCount);
    }

    carryState.carryCards = afterBento;
  }

  // Holds back page 1's own trailing short row (if any) the same way a
  // View More click does — trimmed cards get shown first on the NEXT
  // click instead of either displaying a short row up front or silently
  // dropping them. Server-side rendering can't do this trimming itself
  // (Liquid's paginate tag has no idea how color-splitting will change the
  // card count until it's already rendered everything for the page), so
  // it happens here, right after load, before the shopper has looked at
  // the page.
  function trimInitialRow() {
    const wrapper = document.querySelector('.product-grid-view-more');
    if (!wrapper) return;

    const sectionId = wrapper.dataset.sectionId;
    const container = document.querySelector(`#product-grid[data-id="${sectionId}"]`);
    if (!container || !sectionId) return;

    // A fresh sort/filter means a completely different pagination
    // sequence (the "next page" URLs are different now that sort_by/
    // filters changed) — any carried-over cards or cursor from before
    // this swap belong to the OLD sequence and would be wrong here.
    delete state[sectionId];

    const columnsPerRow = getColumnsPerRow(wrapper);
    if (!columnsPerRow) return;

    // A bento-left block without a bento-right one means this page's own
    // card count landed between 10 and 14 — fetch ahead (bounded) to
    // complete row 3's bento block instead of leaving it as a plain row.
    // Fires independently of the plain-tail handling below, which only
    // ever looks at already-rendered plain cards and is unaffected by it.
    const bentoLeft = container.querySelector(':scope > .bento-grid--left');
    if (bentoLeft) {
      completeBentoRight(sectionId, bentoLeft, columnsPerRow).catch((error) => {
        console.error('[collection-view-more]', error);
      });
    }

    // Only plain cards count/get carried — a bento block (if this page has
    // one) is always already a complete fixed-size unit by construction
    // and is left entirely alone.
    const cards = Array.from(container.querySelectorAll(PLAIN_CARD_SELECTOR));
    const remainder = cards.length % columnsPerRow;
    // Never trim down to nothing — an all-but-empty page 1 (e.g. a heavy
    // color filter leaving fewer than one row's worth of real matches) is
    // a worse outcome than one short row, so this rare case is just left
    // alone rather than hiding every card until the first click.
    if (remainder === 0 || cards.length - remainder <= 0) return;

    const toCarry = cards.slice(cards.length - remainder);
    toCarry.forEach((card) => card.remove());

    getState(sectionId, wrapper.dataset.nextUrl).carryCards = toCarry;
  }

  // Exposed so collection-filter-sort.liquid's Filter/Sort "Apply" AJAX
  // handlers can re-run this exact same trimming on whatever fresh grid
  // content they just swapped in — otherwise a newly sorted/filtered
  // batch (which trimInitialRow never sees, since that only fires once on
  // page load) could land on an uneven count like 29 with no safety net.
  window.trimCollectionGridInitialRow = trimInitialRow;

  document.addEventListener('click', async (event) => {
    const button = event.target.closest('.product-grid-view-more__button');
    if (!button) return;

    const wrapper = button.closest('.product-grid-view-more');
    if (!wrapper || button.disabled) return;
    // collection-grid-shuffle.js's full-catalog shuffle takes over
    // pagination entirely once it's fetched/merged/shuffled every page — it
    // reveals further chunks from that in-memory pool itself, no more real
    // fetches, so this script's own fetch-based handler must stay out of
    // the way.
    if (wrapper.dataset.shuffleFullMode === 'true') return;

    const sectionId = wrapper.dataset.sectionId;
    const container = document.querySelector(`#product-grid[data-id="${sectionId}"]`);
    if (!container || !sectionId) return;

    const columnsPerRow = getColumnsPerRow(wrapper);
    const carryState = getState(sectionId, wrapper.dataset.nextUrl);

    button.disabled = true;
    button.setAttribute('aria-busy', 'true');

    try {
      let combined = carryState.carryCards.splice(0, carryState.carryCards.length);

      if (carryState.nextUrl) {
        const { cards, nextUrl } = await fetchOnePage(carryState.nextUrl, sectionId);
        combined = combined.concat(cards);
        carryState.nextUrl = nextUrl;
      }

      let toShow = combined;
      if (columnsPerRow && carryState.nextUrl) {
        // More pages still exist — trim to a full set of rows and carry
        // the remainder forward again, same as the initial-page trim.
        const remainder = combined.length % columnsPerRow;
        if (remainder > 0 && combined.length - remainder > 0) {
          toShow = combined.slice(0, combined.length - remainder);
          carryState.carryCards = combined.slice(combined.length - remainder);
        }
      }
      // If no more pages exist, show everything gathered (including any
      // leftover) — there's nothing left to borrow from to complete the
      // row, so the true end of the collection is allowed a short last row.

      const tailList = getTailList(wrapper);
      const gridClass = tailList ? tailList.className : 'grid product-grid';
      const newList = document.createElement('ul');
      newList.className = gridClass;
      newList.setAttribute('role', 'list');
      toShow.forEach((item) => newList.appendChild(item));

      if (newList.children.length) {
        container.insertBefore(newList, wrapper);
      }

      if (carryState.nextUrl) {
        wrapper.dataset.nextUrl = carryState.nextUrl;
        button.disabled = false;
        button.removeAttribute('aria-busy');
      } else {
        wrapper.remove();
      }
    } catch (error) {
      button.disabled = false;
      button.removeAttribute('aria-busy');
      console.error('[collection-view-more]', error);
    }
  });

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', trimInitialRow);
  } else {
    trimInitialRow();
  }
})();
